import { Trans } from "@lingui/react/macro"
import { Anchor, Loader, Paper, Text } from "@mantine/core"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/**
 * Floating indicator for the hybrid "remote" (system-audio) channel — shown only
 * in the desktop app while recording. Reassures the user we're capturing the
 * other participants and from which output, with a one-click override. Renders
 * nothing when idle. Dumb: state comes from `useRemoteChannelStreaming`.
 */
export const SystemAudioStatus = ({
  phase,
  sourceName,
  onChangeSource,
  farEndMode,
  meanCoherence,
  speakersLatched,
}: IProps): React.JSX.Element | null => {
  if (phase === "idle") return null

  const coherenceSuffix = typeof meanCoherence === "number" ? ` · ${meanCoherence.toFixed(2)}` : ""

  return (
    <div className={classes.dock}>
      <Paper withBorder shadow="md" className={classes.card}>
        {phase === "detecting" ? (
          <>
            <Loader size="xs" color="brand" />
            <Text size="sm" fw={600} className={classes.text} lineClamp={1}>
              <Trans>Listening for meeting audio…</Trans>
            </Text>
            <Anchor component="button" type="button" size="xs" c="dimmed" onClick={onChangeSource}>
              <Trans>Pick output</Trans>
            </Anchor>
          </>
        ) : (
          <>
            <span className={classes.icon} aria-hidden>
              <span className={classes.wave}>
                <span />
                <span />
                <span />
                <span />
              </span>
            </span>
            <Text size="sm" fw={600} className={classes.text} lineClamp={1}>
              <Trans>Capturing system audio</Trans>
              {sourceName ? ` · ${sourceName}` : ""}
            </Text>
            <Anchor component="button" type="button" size="xs" c="dimmed" onClick={onChangeSource}>
              <Trans>Change</Trans>
            </Anchor>
            {farEndMode ? (
              <Text size="xs" c="dimmed" className={classes.detect} lineClamp={1}>
                {speakersLatched ? (
                  <Trans>far-end in room mic → mic only</Trans>
                ) : farEndMode === "far-end-absent" ? (
                  <Trans>far-end via system audio</Trans>
                ) : (
                  <Trans>detecting far-end…</Trans>
                )}
                {coherenceSuffix}
              </Text>
            ) : null}
          </>
        )}
      </Paper>
    </div>
  )
}
